<template>
  <div
    ref="rootRef"
    class="criteria-list"
  >
    <hr-text-field
      :model-value="search"
      :label="i18n.t('common.buttonSearch')"
      :autofocus="autofocus"
      clearable
      hide-bottom-space
      dense
      class="criteria-list__search"
      @update:model-value="emit('update:search', $event ?? '')"
    >
      <template #prepend>
        <hr-icon name="search" />
      </template>
    </hr-text-field>

    <!--
      A filtered row carries the M3 active indicator as well as its count. The count alone is a
      small piece of text at the far end of a wide row, easy to miss when scanning a list of thirty
      for the two that are doing something.
    -->
    <hr-list class="criteria-list__list">
      <template
        v-for="section in sections"
        :key="section.group ?? 'ungrouped'"
      >
        <hr-list-section-header v-if="section.label">
          {{ section.label }}
        </hr-list-section-header>

        <universal-tooltip
          v-for="criterion in section.criteria"
          :key="criterion.value"
          :show-tooltip="hasTooltip(criterion)"
          :text="criterion.options?.tooltipText ?? ''"
        >
          <hr-list-item
            :data-criterion-row="criterion.value"
            :label="labelFor(criterion)"
            :active="selectionCount(criterion) > 0"
            :disable="isCriterionDisabled(criterion)"
            clickable
            segmented
            @click="emit('select', criterion)"
          >
            <template #trailing>
              <!--
                The count is trailing supporting text, which is what M3 puts on a list row for
                metadata. A badge carries the error role in M3 and has no other, so it says an alert
                where this means a tally. HrItemLabel caption is what trailingText itself renders,
                used through the slot because the row needs the chevron beside it.

                A row of its own: the list item's trailing section is a flex column, so the count
                and the chevron would otherwise stack rather than sit beside each other.
              -->
              <div class="criteria-list__trailing">
                <hr-item-label
                  v-if="selectionCount(criterion) > 0"
                  caption
                  class="criteria-list__count"
                >
                  {{ selectionCount(criterion) }}
                </hr-item-label>
                <hr-icon name="chevron_right" />
              </div>
            </template>
          </hr-list-item>
        </universal-tooltip>
      </template>

      <!--
        Only reached above the flatten threshold, where the groups stay as rows to open rather than
        becoming section headers.
      -->
      <hr-list-item
        v-for="group in navigableGroups"
        :key="group.group"
        :data-group-row="group.group"
        :label="group.label"
        :caption="i18n.t('common.filter.groupCriteriaCount', { count: groupSize(group.group) })"
        :active="groupFilterCount(group.group) > 0"
        icon="folder_open"
        clickable
        segmented
        @click="emit('open-group', group.group)"
      >
        <template #trailing>
          <div class="criteria-list__trailing">
            <hr-item-label
              v-if="groupFilterCount(group.group) > 0"
              caption
              class="criteria-list__count"
            >
              {{ groupFilterCount(group.group) }}
            </hr-item-label>
            <hr-icon name="chevron_right" />
          </div>
        </template>
      </hr-list-item>
    </hr-list>

    <div
      v-if="isEmpty"
      class="criteria-list__empty"
    >
      {{ i18n.t('common.filter.noMatchingCriteria') }}
    </div>
  </div>
</template>

<script setup>
import { i18n } from 'boot/i18n';
import { computed, ref } from 'vue';

import UniversalTooltip from 'components/UI/UniversalTooltip.vue';
import {
  HrIcon, HrItemLabel, HrList, HrListItem, HrListSectionHeader, HrTextField,
} from 'src/design-system';

import shouldAutofocus from './autofocus';
import {
  buildCriteriaSections, getAvailableGroups, getCriterionLabel, getGroupFilterCount,
  getSelectionCount, isCriterionDisabled, shouldFlattenGroups,
} from './criteriaList';

/**
 * The dialog's first pane: every criterion that can be filtered on, with what is already applied
 * shown on the row.
 */
defineOptions({ name: 'CriteriaListPane' });

const props = defineProps({
  /** Every criterion the caller offers. */
  criteria: {
    type: Array,
    required: true,
  },
  /** The filters currently applied, keyed by criterion value. */
  filters: {
    type: Object,
    default: () => ({}),
  },
  /** Group labels the caller declared. No group structure is invented here. */
  filterGroups: {
    type: Array,
    default: () => ([]),
  },
  /** Current search term. Owned by the dialog, which clears it when navigating. */
  search: {
    type: String,
    default: '',
  },
  /** The group being viewed, when the list is deep enough to keep the group level. */
  selectedGroup: {
    type: String,
    default: null,
  },
});

const emit = defineEmits(['select', 'open-group', 'update:search']);

const rootRef = ref(null);

const autofocus = shouldAutofocus();

const isSearching = computed(() => props.search.trim() !== '');

const sections = computed(() => buildCriteriaSections(props.criteria, {
  filterGroups: props.filterGroups,
  search: props.search,
  selectedGroup: props.selectedGroup,
}));

/*
 * Groups become rows to open only on a long list at the top level. While searching they are
 * absent, because search already reaches across every group.
 */
const navigableGroups = computed(() => {
  if (isSearching.value || props.selectedGroup !== null || shouldFlattenGroups(props.criteria)) {
    return [];
  }
  return getAvailableGroups(props.criteria, props.filterGroups);
});

const isEmpty = computed(() => navigableGroups.value.length === 0
  && sections.value.every((section) => section.criteria.length === 0));

const labelFor = (criterion) => getCriterionLabel(criterion, props.filterGroups, isSearching.value);
const selectionCount = (criterion) => getSelectionCount(props.filters[criterion.value]);
const hasTooltip = (criterion) => Boolean(criterion.options?.tooltipText);
const groupSize = (group) => props.criteria.filter((c) => c.group === group).length;
const groupFilterCount = (group) => getGroupFilterCount(props.criteria, props.filters, group);

/*
 * Returning to a row is what keeps the keyboard where the user left it, so the dialog needs a way
 * to reach one. Found by attribute rather than held in a map of refs: the rows are rebuilt
 * whenever the search or the open group changes, and a map would need unmounting bookkeeping to
 * stay in step with them. The attribute sits on the focusable element itself, since that is where
 * Quasar puts the tabindex.
 */
function focusRow(attribute, key) {
  const rows = rootRef.value?.querySelectorAll(`[${attribute}]`) ?? [];
  [...rows].find((row) => row.getAttribute(attribute) === String(key))?.focus();
}

defineExpose({
  /** Moves focus to the row for a criterion, if it is currently listed. */
  focusCriterion: (value) => focusRow('data-criterion-row', value),
  /** Moves focus to the row that opens a group, if the list is showing group rows. */
  focusGroup: (group) => focusRow('data-group-row', group),
});
</script>

<style lang="scss" scoped>
.criteria-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-height: 0;
}

.criteria-list__search {
  flex: none;
}

.criteria-list__list {
  min-height: 0;
}

.criteria-list__trailing {
  display: flex;
  align-items: center;
  gap: 8px;
}

.criteria-list__empty {
  padding: 16px 0;
  color: var(--app-on-surface-variant);
}
</style>
